import React from 'react'
import {
    IconChat,
    IconConfetti,
    IconCursorClick,
    IconEye,
    IconGraph,
    IconInfo,
    IconList,
    IconMagic,
    IconRocket,
    IconSparkles,
    IconSupport,
} from '@posthog/icons'
import { Tweet } from 'components/Tweet'
import { SectionHeading } from 'components/Products/ReaderViewProduct/helpers'
import type { SectionComponentProps } from 'components/Products/ReaderViewProduct/types'
import { applications, keyFeatures } from './support/slides'

const SupportTweet = ({ id }: SectionComponentProps) => (
    <section id={id} className="scroll-mt-20 not-prose">
        <SectionHeading>We didn't pay him to say this</SectionHeading>
        <Tweet
            name="Mathew Chan"
            handle="itsmattchan"
            avatar="https://pbs.twimg.com/profile_images/1905465377280151552/VmDQ3bmf_400x400.jpg"
            avatarAlt="Mathew Chan"
            url="https://x.com/itsmattchan/status/2107843274643788213"
            alertMessage="Go say it on X – this is just a screenshot in HTML form."
        >
            Just had my first holy shit moment with @posthog - I had a feedback form wired up to their support ticket
            feature, and because I had "self-driving" turned on and wired up to my codebase, posthog read the support
            ticket, identified the fix, then opened a PR. This feels like the future. Well done lads, well done. 👏
        </Tweet>
    </section>
)

export const support = {
    Icon: IconSupport,
    name: 'Support',
    handle: 'support',
    slug: 'support',
    teamSlug: 'conversations',
    color: 'blue',
    colorSecondary: 'blue',
    category: 'communication',
    description: 'Built-in customer support with chat widget and unified helpdesk.',
    shortDescription: 'One helpdesk for every customer conversation',
    seo: {
        title: 'Support – Customer support with product context built in | PostHog',
        description:
            'PostHog Support centralizes tickets from your app, email, Slack, and GitHub into a single helpdesk – with session replays, events, and errors attached to each one.',
    },
    /**
     * Sections rendered on the Product surface (`/support`). Each entry resolves
     * to a section template via `templateRegistry[item.template ?? item.slug]`.
     */
    productMenu: [
        {
            slug: 'overview',
            name: 'Overview',
            icon: <IconEye className="size-4" />,
        },
        {
            slug: 'eli5',
            name: 'What does it do?',
            hideFromNav: true,
            group: 'divided',
            icon: <IconInfo className="size-4" />,
        },
        {
            slug: 'use-cases',
            name: 'Who is it for?',
            hideFromNav: true,
            group: 'divided',
            icon: <IconMagic className="size-4" />,
        },
        {
            slug: 'social-proof',
            name: "We didn't pay him to say this",
            hideFromNav: true,
            group: 'divided',
            icon: <IconChat className="size-4" />,
            component: SupportTweet,
        },
        {
            slug: 'applications',
            name: 'How do I use it?',
            group: 'divided',
            icon: <IconCursorClick className="size-4" />,
            props: { slides: applications },
        },
        {
            slug: 'key-features',
            name: 'Key features',
            group: 'divided',
            icon: <IconSparkles className="size-4" />,
            props: { slides: keyFeatures },
        },
        {
            slug: 'ask-anything',
            name: 'AI prompts',
            group: 'divided',
            icon: <IconChat className="size-4" />,
        },
        { slug: 'pairs-with', name: 'Pairs with...', hideFromNav: true, icon: <IconConfetti className="size-4" /> },
        {
            slug: 'posthog-vs',
            name: 'PostHog vs...',
            template: 'comparison-summary',
            group: 'divided',
            icon: <IconList className="size-4" />,
        },
        {
            slug: 'feature-comparison',
            name: 'Feature comparison',
            group: 'divided',
            icon: <IconGraph className="size-4" />,
        },
        { slug: 'getting-started', name: 'Get started', group: 'divided', icon: <IconRocket className="size-4" /> },
    ],
    overview: {
        title: 'One place for every user conversation',
        description: '"Show me which features generate the most support tickets"',
        eli5: 'Support is a helpdesk for solving customer tickets, which arrive from an in-app chat widget, email, Slack, or GitHub. Tickets arrive with full context from other PostHog tools, like session replays, recent events, and errors. Recurring problems get picked up by agents as signal, so PostHog can draft pull requests to ship improvements.',
        textColor: 'text-black', // tw
    },
    hogs: {
        mobileHog: {
            src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog_ai_hogs_d4c45b4550.png',
            alt: 'A support team of hedgehogs at their desks',
        },
    },
    useCases: {
        rows: [
            [
                'Product engineers',
                'Respond to escalated tickets with the session replay and error trace already attached',
            ],
            [
                'Support Engineers',
                'A daily driver for ticket response, with all the customer context you need in PostHog',
            ],
            ['PMs & designers', 'Get reports of where users hit friction, pulled through the MCP'],
            ['Growth Engineers', 'Investigate how support tickets correlate with other metrics, right from Slack'],
            ['QA', 'Turn GitHub issues into tickets to validate user frustration'],
        ],
    },
    ai: {
        image: 'https://res.cloudinary.com/dmukukwp6/image/upload/support_hog_f7ed8447c9.png',
        imageAlt: 'A lifeguard hedgehog with binoculars and a life ring',
        intro: 'Ask PostHog AI to triage, investigate, and answer tickets. Works in PostHog AI (in-app chat), PostHog Desktop, Slack, and your product editor (using the MCP).',
        mcpFeatures: ['conversations'],
        groups: [
            {
                title: 'Triage',
                tool: 'conversations-tickets-list',
                prompts: [
                    'Show me all new tickets from the last 24 hours',
                    'List high-priority tickets that are at risk of breaching SLA',
                    'Which open tickets mention the checkout flow?',
                    'Which tickets are assigned to me and still unanswered?',
                ],
            },
            {
                title: 'Investigate',
                tool: 'conversations-tickets-retrieve',
                prompts: [
                    'Summarize what the customer on ticket #482 did before they wrote in',
                    'Find the session replay attached to this ticket and tell me where it went wrong',
                    'Are there errors in this customer’s session that match an existing issue?',
                    'Have we heard from anyone else at this customer’s company recently?',
                ],
            },
            {
                title: 'Respond',
                tool: 'conversations-tickets-reply-create',
                prompts: [
                    'Draft a reply to ticket #123 explaining the workaround',
                    'Reply to this ticket with a link to the relevant docs and mark it resolved',
                ],
            },
            {
                title: 'Analyze',
                prompts: [
                    'Which feature generates the most tickets?',
                    'How does ticket volume this week compare to last week?',
                    'Do customers who file tickets retain better or worse than those who don’t?',
                ],
            },
        ],
    },
    comparison: {
        summary: {
            them: [
                {
                    title: 'You need voice, phone, or call center support channels',
                },
                {
                    title: 'You want a mature ecosystem of helpdesk add-ons, marketplaces, and certified consultants',
                },
            ],
            us: [
                {
                    title: 'Tickets arrive with session replays, events, and errors attached – no reproduction steps required',
                },
                {
                    title: 'Support data lives next to your product analytics',
                    subtitle: 'Correlate tickets with retention, funnels, and flags',
                },
                {
                    title: 'Recurring issues become Self-driving reports and draft PRs',
                },
                {
                    title: 'Core features are free',
                    subtitle: 'No per-seat pricing',
                },
            ],
        },
        companies: [
            {
                name: 'Zendesk',
                key: 'zendesk',
            },
            {
                name: 'Intercom',
                key: 'intercom',
            },
            {
                name: 'PostHog',
                key: 'posthog',
            },
        ],
        rows: ['support'],
    },
    pairsWith: [
        {
            slug: 'product-analytics',
            description: 'Investigate the numbers behind each ticket, including how they correlate with retention',
        },
        {
            slug: 'session-replay',
            description: 'Watch exactly what a customer did before they wrote in – no reproduction steps needed',
        },
        {
            slug: 'error-tracking',
            description: 'See the full stack trace behind every error to find the root cause of each ticket',
        },
    ],
}
